import { format, parseISO } from 'date-fns';
import { EChartsOption } from 'echarts';
import {
  StatsChartVariant,
  StatsFunnelBarModel,
  StatsKpiTrendModel,
} from '../admin-stats-aggregate/admin-stats-aggregate.types';

export interface StatsPercentChange {
  percent: number | null;
  direction: 'up' | 'down' | 'flat';
}

export function calcPercentChange(current: number, previous: number): StatsPercentChange {
  if (previous === 0) {
    if (current === 0) {
      return { percent: 0, direction: 'flat' };
    }
    return { percent: null, direction: 'up' };
  }

  const percent = ((current - previous) / previous) * 100;
  if (percent > 0) {
    return { percent, direction: 'up' };
  }
  if (percent < 0) {
    return { percent, direction: 'down' };
  }
  return { percent: 0, direction: 'flat' };
}

export function formatPercentChange(change: StatsPercentChange): string {
  if (change.percent === null) {
    return 'New';
  }

  const rounded = Math.abs(change.percent);
  const formatted = rounded >= 10 ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${formatted}%`;
}

export function getKpiTrend(current: number, previous: number): StatsKpiTrendModel {
  const change = calcPercentChange(current, previous);
  const arrow = change.direction === 'up' ? '▲' : change.direction === 'down' ? '▼' : '•';
  const trendClass =
    change.direction === 'up'
      ? 'text-emerald-600 dark:text-emerald-400'
      : change.direction === 'down'
        ? 'text-rose-600 dark:text-rose-400'
        : 'text-zinc-600 dark:text-zinc-400';

  return {
    arrow,
    percentLabel: formatPercentChange(change),
    comparisonLabel: `vs ${previous.toLocaleString()} previous period`,
    trendClass,
  };
}

export function buildFunnelBars(
  baselineValue: number,
  steps: { label: string; value: number }[],
): StatsFunnelBarModel[] {
  if (baselineValue <= 0) {
    return steps.map((step) => ({
      label: step.label,
      value: step.value,
      percentOfBaseline: 0,
    }));
  }

  return steps.map((step) => ({
    label: step.label,
    value: step.value,
    percentOfBaseline: Math.min(100, (step.value / baselineValue) * 100),
  }));
}

export function buildDailyBarChart(
  points: { date: string; value: number }[],
): { chartOption: EChartsOption; chartXaxisData: string[]; hasData: boolean } {
  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date));
  const hasData = sorted.some((point) => point.value > 0);
  const chartXaxisData = sorted.map((point) => format(parseISO(point.date), 'EEE'));

  const chartOption: EChartsOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '3%', bottom: '12%', top: '8%', containLabel: true },
    xAxis: { type: 'category', data: chartXaxisData },
    yAxis: { type: 'value' },
    series: [
      {
        type: 'bar',
        data: sorted.map((point) => point.value),
      },
    ],
  };

  return { chartOption, chartXaxisData, hasData };
}

export function creatorInitials(firstName: string, lastName: string): string {
  const first = firstName.trim().charAt(0);
  const last = lastName.trim().charAt(0);
  return `${first}${last}`.toUpperCase() || '?';
}

export function mapTopCreatorRows(
  rows: {
    creatorId: string;
    firstName: string;
    lastName: string;
    photoURL?: string;
    appearedInSearches: number;
    profileVisits: number;
    invitedToBrief?: number;
    hiredForBrief?: number;
  }[],
  metric: import('../admin-stats-aggregate/admin-stats-aggregate.types').CreatorStatsLeaderboardMetric = 'appearedInSearches',
  secondaryMetric: import('../admin-stats-aggregate/admin-stats-aggregate.types').CreatorStatsLeaderboardMetric = 'profileVisits',
): import('../admin-stats-aggregate/admin-stats-aggregate.types').StatsTopCreatorRowModel[] {
  const max = Math.max(...rows.map((row) => row[metric] ?? 0), 1);

  return rows.map((row) => {
    const metricValue = row[metric] ?? 0;
    return {
      creatorId: row.creatorId,
      firstName: row.firstName,
      lastName: row.lastName,
      photoURL: row.photoURL,
      appearedInSearches: row.appearedInSearches ?? 0,
      profileVisits: row.profileVisits ?? 0,
      invitedToBrief: row.invitedToBrief ?? 0,
      hiredForBrief: row.hiredForBrief ?? 0,
      metricValue,
      secondaryValue: row[secondaryMetric] ?? 0,
      barPercent: (metricValue / max) * 100,
    };
  });
}

export function companyInitials(companyName: string): string {
  const parts = companyName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

export function mapMostActiveBusinessRows(
  rows: {
    statDate: string;
    businessAccountId: string;
    companyName: string;
    agencyId: string | null;
    agencyName: string | null;
    searches: number;
    profileVisits: number;
    invitesSent?: number;
    hires?: number;
  }[],
  metric: import('../admin-stats-aggregate/admin-stats-aggregate.types').BusinessStatsLeaderboardMetric = 'searches',
  secondaryMetric: import('../admin-stats-aggregate/admin-stats-aggregate.types').BusinessStatsLeaderboardMetric = 'profileVisits',
): import('../admin-stats-aggregate/admin-stats-aggregate.types').StatsMostActiveBusinessRowModel[] {
  const max = Math.max(...rows.map((row) => row[metric] ?? 0), 1);

  return rows.map((row) => {
    const metricValue = row[metric] ?? 0;
    return {
      statDate: row.statDate,
      businessAccountId: row.businessAccountId,
      companyName: row.companyName,
      agencyId: row.agencyId,
      agencyName: row.agencyName,
      searches: row.searches ?? 0,
      profileVisits: row.profileVisits ?? 0,
      invitesSent: row.invitesSent ?? 0,
      hires: row.hires ?? 0,
      metricValue,
      secondaryValue: row[secondaryMetric] ?? 0,
      barPercent: (metricValue / max) * 100,
    };
  });
}

export function chartVariantForMetricKey(key: string, isDarkMode: boolean): StatsChartVariant {
  const isPink =
    key === 'invitedToBrief' || key === 'hiredForBrief' || key === 'invitesSent' || key === 'hires';

  if (isDarkMode) {
    return isPink ? 'timelineBarPinkDark' : 'timelineBarDark';
  }

  return isPink ? 'timelineBarPink' : 'timelineBarPurple';
}

export function kpiAccentDotClass(accent: 'violet' | 'pink'): string {
  return accent === 'pink' ? 'bg-fuchsia-400' : 'bg-violet-400';
}

export function funnelBarClass(index: number, total: number): string {
  if (index === total - 1) {
    return 'bg-gradient-to-r from-fuchsia-500 to-violet-600';
  }

  if (index >= total - 2) {
    return 'bg-gradient-to-r from-fuchsia-500/90 to-violet-500';
  }

  return 'bg-gradient-to-r from-violet-500 to-indigo-500';
}

export function topCreatorBarClass(): string {
  return 'bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500';
}
